import { useMutation } from '@apollo/client';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { DeletePersonMfa, MfaType } from 'Apollo';
import { PersonContext } from 'Context/CurrentPerson';
import useNotification from 'Hooks/useNotification';
import useTranslate from 'Hooks/useTranslate';
import { TProfileManagerMessagesKeys } from 'Languages/TProfileManagerMessages';
import { useContext } from 'react';
import { generatePath, useHistory } from 'react-router';
import { PrivateRoutes } from 'Router/Routes';
import { GreyLabel } from 'Theme/components';
import joinURL from 'url-join';
import { MfaTransferTotpEmail } from './MfaTransferTotpEmail';

interface MfaDeleteDisplayProps {
  readonly isCurrentUser: boolean;
  readonly userImmutableId: string;
  readonly mfaType: MfaType;
  readonly isForcedByOrganization: boolean;
}

function MfaDeleteDisplay({
  isCurrentUser,
  userImmutableId,
  mfaType,
  isForcedByOrganization
}: MfaDeleteDisplayProps) {
  const { currentPerson, currentPersonRoles } = useContext(PersonContext);
  const { onSuccess, onError } = useNotification();
  const ts = useTranslate();
  const history = useHistory();

  const onCompleted = () => {
    onSuccess('page.deletePersonMfa.success');
    history.goBack();
  };

  useMutation(DeletePersonMfa, {
    variables: {
      immutableId: userImmutableId,
      mfaType: mfaType
    },
    onError: error => {
      onError(error);
    },
    onCompleted
  });

  let redirectUri: string;

  redirectUri = generatePath(PrivateRoutes.deletePersonMfa.path, {
    accountCode: currentPerson.account ? currentPerson.account.code : 'all',
    immutableId: userImmutableId,
    targetedMfa: mfaType
  });

  // User deleting their own 2FA must be redirected to the MFA challenge page
  // to confirm their identity before deletion
  if (isCurrentUser) {
    return (
      <Tooltip
        title={
          isForcedByOrganization
            ? ts('page.userProfile.security.mfa.deleteNotAllowed')
            : ''
        }
      >
        <span>
          <IconButton
            data-testid="delete-totp-button"
            color="error"
            href={`${AppSettings.API_ENDPOINT_URI
              }api/mfa/challenge?redirectUri=${encodeURIComponent(
                joinURL('/', redirectUri)
              )}&targetOperation=${mfaType}`}
            disabled={isForcedByOrganization}
          >
            <DeleteForeverIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  // Super admins and organization owners can delete other users' 2FA without MFA challenge
  if (
    currentPersonRoles.canWriteAll ||
    currentPersonRoles.isOrganizationOwner
  ) {
    return (
      <IconButton
        data-testid="delete-totp-button"
        color="error"
        href={redirectUri}
      >
        <DeleteForeverIcon />
      </IconButton>
    );
  }

  return null;
}

interface MfaDisplayProps {
  readonly isCurrentUser: boolean;
  readonly userImmutableId: string;
  readonly userEmail: string;
  readonly isMfaActive: boolean;
  readonly type: MfaType;
  readonly isForcedByOrganization: boolean;
  readonly canTransfertToEmail: boolean;
  readonly canDelete: boolean;
  readonly refreshUserProfile: () => Promise<void>;
  readonly emailMfaAddress?: string;
}

export function MfaReadyDisplay({
  isCurrentUser,
  userImmutableId,
  userEmail,
  isMfaActive,
  type,
  isForcedByOrganization,
  canTransfertToEmail,
  canDelete,
  refreshUserProfile,
  emailMfaAddress
}: MfaDisplayProps) {
  const ts = useTranslate();

  let mfaReadyLabelKey = `page.userProfile.security.mfa.${type.toLowerCase()}.ready` as TProfileManagerMessagesKeys;
  const mfaReadyLabelValues =
    type === MfaType.Email ? { emailMfaAddress } : undefined;
  return (
    <Box display="flex" flexDirection="column">
      <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
        <Button color="success" disabled={true}>
          <Box component="span">
            {isMfaActive ? (
              <>
                {ts(mfaReadyLabelKey)}
                {mfaReadyLabelValues?.emailMfaAddress != null && (
                  <>
                    {' : '}
                    <GreyLabel>
                      {mfaReadyLabelValues.emailMfaAddress}
                    </GreyLabel>
                  </>
                )}
              </>
            ) : mfaReadyLabelValues?.emailMfaAddress != null ? (
              <Box component="span" sx={{ fontWeight: 'normal' }}>
                <Box component="span">
                  {ts('page.userProfile.security.mfa.mfaInactive')}
                </Box>
                <br />
                <Box component="span">
                  {ts('page.userProfile.security.mfa.emailMfaAddress')}{' '}
                </Box>
                <Box component="span">
                  {mfaReadyLabelValues.emailMfaAddress}
                </Box>
              </Box>
            ) : (
              ts('page.userProfile.security.mfa.mfaInactive')
            )}
          </Box>
        </Button>
        {canDelete && (
          <MfaDeleteDisplay
            isCurrentUser={isCurrentUser}
            userImmutableId={userImmutableId}
            mfaType={type}
            isForcedByOrganization={isForcedByOrganization}
          />
        )}
      </Box>
      {canTransfertToEmail && (
        <MfaTransferTotpEmail
          userImmutableId={userImmutableId}
          userEmail={userEmail}
          refreshUserProfile={refreshUserProfile}
        />
      )}
    </Box>
  );
}
